import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { confirmGoatProfilePhone } from "../lib/goat_profile_phone_confirm"
import { setButtonLoading } from "../lib/button_loading"

// Profile "Change Phone" modal: enter a new number -> request OTP (stores pending_phone,
// rejects duplicates) -> enter the 6-digit code -> verify (promotes pending_phone to phone).
// Messages and any "Sign in instead?" link are built from DOM nodes (never innerHTML /
// attribute interpolation) per the project's XSS-in-attributes rule.
// Busy (BUTTON-8): Send Code and Verify & Save keep their label while a request
// runs — shared spinner, aria-busy, second click blocked (lib/button_loading).
export default class extends Controller {
  static targets = [
    "phoneInput", "sendBtn", "phoneStep",
    "codeStep", "codeInput", "verifyBtn", "message",
    "syncCheckbox"
  ]
  static values = { requestUrl: String, verifyUrl: String, returnTo: String }

  async requestChange() {
    const phone = this.phoneInputTarget.value.trim()
    if (!phone) { this.showMessage("Please enter a phone number.", "error"); return }

    setButtonLoading(this.sendBtnTarget, true)
    this.clearMessage()
    let acknowledge = false
    try {
      // #12458: the "also use this number on my thank-you page" choice rides the
      // request and is applied when this number is verified. A GOAT-owned number
      // is refused until the agent confirms the warning (#18649).
      for (;;) {
        const body = {
          phone,
          sync_thank_you_phone: this.syncThankYouPhoneChecked() ? "1" : "0",
          acknowledge_goat_number: acknowledge ? "1" : "0"
        }
        const response = await post(this.requestUrlValue, { body, responseKind: "json" })
        const data = await response.json
        if (data.error_code === "goat_number" && !acknowledge) {
          setButtonLoading(this.sendBtnTarget, false)
          const useAnyway = await confirmGoatProfilePhone()
          if (!useAnyway) return
          acknowledge = true
          setButtonLoading(this.sendBtnTarget, true)
          continue
        }
        if (data.success) {
          // Body block AND its footer buttons carry each step target (MODAL-2: the
          // actions live in the shared footer), so the step swaps as one.
          this.phoneStepTargets.forEach((el) => el.classList.add("d-none"))
          this.codeStepTargets.forEach((el) => el.classList.remove("d-none"))
          this.codeInputTarget.focus()
          this.showMessage("Code sent! Check your phone.", "success")
        } else {
          this.showError(data)
        }
        return
      }
    } catch {
      this.showMessage("Something went wrong. Please try again.", "error")
    } finally {
      setButtonLoading(this.sendBtnTarget, false)
    }
  }

  async verifyChange() {
    const code = this.codeInputTarget.value.trim()
    if (code.length < 6) { this.showMessage("Please enter the 6-digit code.", "error"); return }

    setButtonLoading(this.verifyBtnTarget, true)
    this.clearMessage()
    try {
      const response = await post(this.verifyUrlValue, { body: { code }, responseKind: "json" })
      const data = await response.json
      if (data.success) {
        this.showMessage("Phone verified and updated!", "success")
        window.location.assign(this.returnToValue)
      } else {
        this.showMessage(data.message || "Invalid code. Please try again.", "error")
        setButtonLoading(this.verifyBtnTarget, false)
      }
    } catch {
      this.showMessage("Something went wrong. Please try again.", "error")
      setButtonLoading(this.verifyBtnTarget, false)
    }
  }

  syncThankYouPhoneChecked() {
    return this.hasSyncCheckboxTarget && this.syncCheckboxTarget.checked
  }

  resend() {
    this.codeInputTarget.value = "" // drop any stale/wrong code before re-sending
    this.requestChange()
  }

  // server strings via textContent; link href via setAttribute — never innerHTML
  showError(data) {
    this.clearMessage()
    this.messageTarget.classList.remove("d-none")
    this.messageTarget.className = "gl-onboarding-message error mt-3"
    this.messageTarget.replaceChildren(document.createTextNode(data.message || "Could not send a code."))
    if (data.sign_in_url) {
      const link = document.createElement("a")
      link.setAttribute("href", data.sign_in_url)
      link.className = "gl-onboarding-link ms-1"
      link.textContent = "Sign in instead?"
      this.messageTarget.append(" ", link)
    }
  }

  showMessage(text, type) {
    this.messageTarget.classList.remove("d-none")
    this.messageTarget.className = `gl-onboarding-message ${type} mt-3`
    this.messageTarget.replaceChildren(document.createTextNode(text))
  }

  clearMessage() {
    this.messageTarget.classList.add("d-none")
    this.messageTarget.replaceChildren()
  }
}
